/**
 * "Is the person in the middle of something?" for shell sync (bug bash
 * SW-1): a full load after the UI switch flips must never throw away text
 * nobody sent, or an upload in flight. lib/ui-variant/shell-sync.ts and
 * WebDeploySync ask this before reloading on their own; when the answer is
 * yes they leave the change for the next navigation and show the "new
 * version" notice instead (components/ui-shell-update-notice.tsx).
 *
 * Unsent text is read from the DOM, so it covers every composer and form in
 * both interfaces without each one registering:
 * - a textarea or a text-like input with something typed in it;
 * - a contenteditable editor (the Encuentro post and comment editors) with
 *   text in it;
 * - a file input with a file chosen;
 * - anything marked `data-unsent-work="true"` (an editor that keeps its text
 *   elsewhere can say so).
 * Search boxes and comboboxes (`type="search"`, `role="search|searchbox|combobox"`)
 * do not count: their text lives in the address and survives a reload. Neither
 * does anything under `data-unsent-ignore`, `hidden`, `inert` or `aria-hidden`.
 * A pre-filled edit form counts as unsent: that errs on the side of a notice
 * instead of a reload.
 *
 * Uploads are counted where they start: `fetch` bodies (shell-sync's wrapper)
 * and `XMLHttpRequest.send` (the video upload, lib/feed/feed-video-upload.ts)
 * that carry a file. The count lives on `window` because instrumentation-client
 * and the app bundle may hold separate copies of this module.
 */

export const UNSENT_WORK_ATTRIBUTE = 'data-unsent-work'
export const UNSENT_IGNORE_ATTRIBUTE = 'data-unsent-ignore'

/** Input types whose value is text a person typed. */
const TEXT_INPUT_TYPES = new Set(['text', 'email', 'url', 'tel', 'number', 'password'])
const NOT_TYPED_ROLES = new Set(['search', 'searchbox', 'combobox'])
const IGNORED_ANCESTOR = `[${UNSENT_IGNORE_ATTRIBUTE}], [role="search"], [hidden], [inert], [aria-hidden="true"]`

function isIgnored(el: Element): boolean {
  return NOT_TYPED_ROLES.has(el.getAttribute('role') ?? '') || el.closest(IGNORED_ANCESTOR) !== null
}

function isEditableHost(el: Element): boolean {
  const value = el.getAttribute('contenteditable')
  return value !== null && value.toLowerCase() !== 'false'
}

/** Text typed and not sent, anywhere in this document. */
export function hasUnsentText(doc: Document): boolean {
  for (const el of Array.from(doc.querySelectorAll('textarea, input'))) {
    const field = el as HTMLInputElement | HTMLTextAreaElement
    if (field.disabled || field.readOnly) continue
    if (el.tagName === 'INPUT') {
      const input = field as HTMLInputElement
      const type = (input.getAttribute('type') ?? 'text').toLowerCase()
      if (type === 'file') {
        if (input.files && input.files.length > 0 && !isIgnored(input)) return true
        continue
      }
      if (!TEXT_INPUT_TYPES.has(type)) continue
    }
    if (!field.value.trim() || isIgnored(field)) continue
    return true
  }
  for (const el of Array.from(doc.querySelectorAll('[contenteditable]'))) {
    if (!isEditableHost(el)) continue
    // Only the outermost editable host speaks for its text.
    const parent = el.parentElement?.closest('[contenteditable]')
    if (parent && isEditableHost(parent)) continue
    if (!el.textContent?.trim() || isIgnored(el)) continue
    return true
  }
  return doc.querySelector(`[${UNSENT_WORK_ATTRIBUTE}="true"]`) !== null
}

// ---------------------------------------------------------------------------
// Uploads in flight
// ---------------------------------------------------------------------------

const UPLOADS_KEY = '__alumniaUploadsInFlight'

type UploadWindow = { [UPLOADS_KEY]?: number }

/** A request body that carries a file: a Blob, or a FormData with one. */
export function isUploadBody(body: unknown): boolean {
  if (typeof Blob !== 'undefined' && body instanceof Blob) return true
  if (typeof FormData !== 'undefined' && body instanceof FormData) {
    for (const [, value] of body.entries()) {
      if (typeof value !== 'string') return true
    }
  }
  return false
}

export function uploadsInFlight(win: object): number {
  return (win as UploadWindow)[UPLOADS_KEY] ?? 0
}

/** Counts one upload until `done` is called (once). */
export function beginUpload(win: object): () => void {
  const w = win as UploadWindow
  w[UPLOADS_KEY] = (w[UPLOADS_KEY] ?? 0) + 1
  let finished = false
  return () => {
    if (finished) return
    finished = true
    w[UPLOADS_KEY] = Math.max(0, (w[UPLOADS_KEY] ?? 1) - 1)
  }
}

/** Counts the XMLHttpRequest uploads of this window. Idempotent. */
export function trackXhrUploads(win: { XMLHttpRequest?: typeof XMLHttpRequest }): void {
  const Xhr = win.XMLHttpRequest
  if (!Xhr) return
  const proto = Xhr.prototype as XMLHttpRequest & { __alumniaUploadTracked?: boolean }
  if (proto.__alumniaUploadTracked) return
  proto.__alumniaUploadTracked = true
  const originalSend = proto.send
  proto.send = function trackedSend(this: XMLHttpRequest, body?: Document | XMLHttpRequestBodyInit | null) {
    if (isUploadBody(body)) {
      const done = beginUpload(win)
      this.addEventListener('loadend', done, { once: true })
    }
    return originalSend.call(this, body)
  }
}

/** Typed text not sent, or a file still uploading: no full load now. */
export function hasUnsentWork(win: { document: Document }): boolean {
  return uploadsInFlight(win) > 0 || hasUnsentText(win.document)
}
